import { type FormEvent } from "react";
import { Spinner } from "../UI/Spinner";
import type { MarkerCategory } from "../../types/domain.types";
import {
  MARKER_CATEGORIES,
  MARKER_CATEGORY_ICON,
  MARKER_CATEGORY_LABEL,
} from "../../utils/markerCategory";

interface Props {
  qrCode: string;
  onQrCodeChange: (code: string) => void;
  category: MarkerCategory | null;
  onCategoryChange: (cat: MarkerCategory) => void;
  coord: { x: number; y: number } | null;
  comment: string;
  onCommentChange: (comment: string) => void;
  submitting: boolean;
  error: string | null;
  submitLabel?: string;
  onCancel: () => void;
  onSubmit: (input: {
    qrCode: string;
    category: MarkerCategory;
    comment: string;
  }) => void;
}

export function MarkerForm({
  qrCode,
  onQrCodeChange,
  category,
  onCategoryChange,
  coord,
  comment,
  onCommentChange,
  submitting,
  error,
  submitLabel = "Guardar marcador",
  onCancel,
  onSubmit,
}: Props) {
  const disabled =
    submitting || !coord || !qrCode.trim() || !category;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (disabled || !category) return;
    onSubmit({
      qrCode: qrCode.trim(),
      category,
      comment: comment.trim(),
    });
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-3 rounded-md bg-slate-900/80 p-4 ring-1 ring-slate-700"
    >
      <div>
        <label className="text-xs uppercase tracking-wide text-slate-400">
          Código QR
        </label>
        <input
          value={qrCode}
          onChange={(e) => onQrCodeChange(e.target.value)}
          placeholder="ID o código QR"
          className="mt-1 w-full rounded-md bg-slate-800 px-3 py-2 text-sm text-slate-100 ring-1 ring-slate-700 focus:outline-none focus:ring-brand-light"
        />
      </div>

      <div>
        <label className="text-xs uppercase tracking-wide text-slate-400">
          Tipo
        </label>
        <div className="mt-1 grid grid-cols-4 gap-2">
          {MARKER_CATEGORIES.map((c) => {
            const active = category === c;
            return (
              <button
                key={c}
                type="button"
                onClick={() => onCategoryChange(c)}
                title={MARKER_CATEGORY_LABEL[c]}
                className={`flex flex-col items-center gap-1 rounded-md border px-2 py-2 transition ${
                  active
                    ? "border-brand-light bg-brand-light/10 ring-2 ring-brand-light"
                    : "border-slate-700 bg-slate-800 hover:bg-slate-700"
                }`}
              >
                <img
                  src={MARKER_CATEGORY_ICON[c]}
                  alt={MARKER_CATEGORY_LABEL[c]}
                  className="h-8 w-8 object-contain"
                />
                <span className="text-[10px] font-semibold text-slate-200">
                  {MARKER_CATEGORY_LABEL[c]}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-400">
            Coord X
          </label>
          <input
            readOnly
            value={coord ? coord.x.toFixed(2) : ""}
            placeholder="-"
            className="mt-1 w-full rounded-md bg-slate-800 px-3 py-2 text-sm text-slate-100 ring-1 ring-slate-700"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-400">
            Coord Y
          </label>
          <input
            readOnly
            value={coord ? coord.y.toFixed(2) : ""}
            placeholder="-"
            className="mt-1 w-full rounded-md bg-slate-800 px-3 py-2 text-sm text-slate-100 ring-1 ring-slate-700"
          />
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-wide text-slate-400">
          Comentario
        </label>
        <textarea
          value={comment}
          onChange={(e) => onCommentChange(e.target.value)}
          maxLength={500}
          rows={3}
          className="mt-1 w-full resize-none rounded-md bg-slate-800 px-3 py-2 text-sm text-slate-100 ring-1 ring-slate-700 focus:outline-none focus:ring-brand-light"
          placeholder="Notas opcionales (máx. 500)"
        />
      </div>

      {error && (
        <p role="alert" className="text-xs text-red-400">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="rounded-md border border-slate-700 px-3 py-2 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={disabled}
          className="flex items-center gap-2 rounded-md bg-brand-light px-3 py-2 text-xs font-semibold text-white hover:bg-brand disabled:opacity-50"
        >
          {submitting && <Spinner size={14} />}
          {submitLabel}
        </button>
      </div>
    </form>
  );
}
